import { formatIban, isValidIban, normalizeIban } from "common/Iban";
import { Account } from "models/Account";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Alert } from "reactstrap";
import { bindActionCreators } from "redux";
import { vatCheck } from "services/AccountApi";
import { editCustomerIban, editCustomerVat } from "storeAccount/actions/Customers";
import { useAccountTranslation } from "../translations";

interface Props {
    customer: Account;
    onCustomerChange: (customer: Account) => void;
}

interface Actions {
    editCustomerIban: typeof editCustomerIban;
    editCustomerVat: typeof editCustomerVat;
}

type EditField = "iban" | "vat" | null;
type VatStatus = "checking" | "invalid" | "valid" | "unavailable";

export const AccountCustomerVatCheckCard: React.FC<Props & Actions> = ({
    customer,
    editCustomerIban,
    editCustomerVat,
    onCustomerChange,
}) => {
    const { t } = useAccountTranslation();
    const customerVat = customer.vat_number ?? "";
    const bankAccounts = customer.bank_accounts ?? [];
    const customerIban = (bankAccounts.find(bankAccount => bankAccount.primary) ?? bankAccounts[0])?.iban ?? "";
    const [vat, setVat] = useState(customerVat);
    const [iban, setIban] = useState(customerIban);
    const [draft, setDraft] = useState("");
    const [editing, setEditing] = useState<EditField>(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ color: "danger" | "success"; text: string } | null>(null);
    const [vatStatus, setVatStatus] = useState<VatStatus>(vat ? "checking" : "unavailable");
    const [vatDetails, setVatDetails] = useState("");

    useEffect(() => {
        setVat(customerVat);
    }, [customerVat]);

    useEffect(() => {
        setIban(customerIban);
    }, [customerIban]);

    useEffect(() => {
        if (!vat.trim()) {
            setVatStatus("unavailable");
            setVatDetails("");
            return;
        }

        let active = true;
        setVatStatus("checking");
        vatCheck(vat.trim())
            .then(result => {
                if (!active) return;
                if (!result.isValid) {
                    setVatStatus("invalid");
                    setVatDetails("");
                    return;
                }
                setVatStatus("valid");
                setVatDetails([result.name, result.address?.replace(/\n/g, ", ")].filter(Boolean).join(" · "));
            })
            .catch(() => {
                if (active) {
                    setVatStatus("invalid");
                    setVatDetails("");
                }
            });
        return () => { active = false; };
    }, [vat]);

    const startEditing = (field: Exclude<EditField, null>) => {
        setMessage(null);
        setEditing(field);
        setDraft(field === "vat" ? vat : formatIban(iban));
    };

    const cancelEditing = () => {
        setEditing(null);
        setDraft("");
        setMessage(null);
    };

    const saveVat = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const normalized = draft.trim().toUpperCase();
            const updated = await editCustomerVat(customer.id, normalized);
            onCustomerChange(updated);
            setVat(normalized);
            setEditing(null);
            setMessage({ color: "success", text: t("companyDetailsSaved") });
        } catch {
            setMessage({ color: "danger", text: t("companyDetailsSaveError") });
        } finally {
            setBusy(false);
        }
    };

    const saveIban = async () => {
        if (!isValidIban(draft)) return;
        setBusy(true);
        setMessage(null);
        try {
            const normalized = normalizeIban(draft);
            const updated = await editCustomerIban(customer.id, normalized);
            onCustomerChange(updated);
            setIban(normalized);
            setEditing(null);
            setMessage({ color: "success", text: t("companyDetailsSaved") });
        } catch {
            setMessage({ color: "danger", text: t("companyDetailsSaveError") });
        } finally {
            setBusy(false);
        }
    };

    const vatStatusText = vatStatus === "checking"
        ? t("checking")
        : vatStatus === "valid"
            ? t("vatValid")
            : vatStatus === "invalid"
                ? t("vatInvalid")
                : t("vatInfoUnavailable");
    const ibanValid = isValidIban(iban);
    const draftIbanValid = isValidIban(draft);

    return <div className="portal-company-financials">
        <h3>{t("paymentDetails")}</h3>
        {message && <Alert color={message.color} fade={false} className="py-2 mb-2">{message.text}</Alert>}

        <div className="portal-company-field">
            <div className="portal-company-field-copy">
                <span>{t("vatNumber")}</span>
                {editing === "vat" ? <div className="portal-company-editor">
                    <label className="visually-hidden" htmlFor="portal-company-vat">{t("vatNumber")}</label>
                    <input
                        id="portal-company-vat"
                        className="form-control"
                        value={draft}
                        onChange={event => setDraft(event.target.value)}
                        autoFocus
                    />
                    <div className="portal-button-row">
                        <button type="button" className="portal-button portal-button-primary" disabled={busy || !draft.trim()} onClick={saveVat}>
                            {busy ? t("saving") : t("save")}
                        </button>
                        <button type="button" className="portal-button portal-button-secondary" disabled={busy} onClick={cancelEditing}>
                            {t("cancel")}
                        </button>
                    </div>
                </div> : <>
                    <strong className="font-monospace">{vat || "—"}</strong>
                    <small className={`portal-validation portal-validation--${vatStatus === "valid" ? "valid" : vatStatus === "checking" || vatStatus === "unavailable" ? "neutral" : "invalid"}`}>
                        <i className={`mdi ${vatStatus === "valid" ? "mdi-check-circle-outline" : vatStatus === "checking" ? "mdi-loading mdi-spin" : vatStatus === "invalid" ? "mdi-alert-circle-outline" : "mdi-information-outline"}`} aria-hidden="true" />
                        {vatStatusText}
                    </small>
                    {vatDetails && <small className="portal-company-registered-details">{vatDetails}</small>}
                </>}
            </div>
            {editing !== "vat" && <button type="button" className="portal-button portal-button-secondary" onClick={() => startEditing("vat")}>
                <i className="mdi mdi-pencil-outline" aria-hidden="true" />{t("edit")}
            </button>}
        </div>

        <div className="portal-company-field">
            <div className="portal-company-field-copy">
                <span>{t("iban")}</span>
                {editing === "iban" ? <div className="portal-company-editor">
                    <label className="visually-hidden" htmlFor="portal-company-iban">{t("iban")}</label>
                    <input
                        id="portal-company-iban"
                        className={`form-control${draft && !draftIbanValid ? " is-invalid" : ""}`}
                        value={draft}
                        onChange={event => setDraft(event.target.value.toUpperCase())}
                        aria-describedby="portal-company-iban-feedback"
                        autoComplete="off"
                        autoFocus
                    />
                    <small
                        id="portal-company-iban-feedback"
                        className={`portal-validation portal-validation--${draftIbanValid ? "valid" : "invalid"}`}
                    >
                        <i className={`mdi ${draftIbanValid ? "mdi-check-circle-outline" : "mdi-alert-circle-outline"}`} aria-hidden="true" />
                        {draftIbanValid ? t("ibanValid") : t("ibanInvalid")}
                    </small>
                    <div className="portal-button-row">
                        <button type="button" className="portal-button portal-button-primary" disabled={busy || !draftIbanValid} onClick={saveIban}>
                            {busy ? t("saving") : t("save")}
                        </button>
                        <button type="button" className="portal-button portal-button-secondary" disabled={busy} onClick={cancelEditing}>
                            {t("cancel")}
                        </button>
                    </div>
                </div> : <>
                    <strong className="font-monospace">{iban ? formatIban(iban) : "—"}</strong>
                    <small className={`portal-validation portal-validation--${ibanValid ? "valid" : "invalid"}`}>
                        <i className={`mdi ${ibanValid ? "mdi-check-circle-outline" : "mdi-alert-circle-outline"}`} aria-hidden="true" />
                        {ibanValid ? t("ibanValid") : iban ? t("ibanInvalid") : t("ibanMissing")}
                    </small>
                </>}
            </div>
            {editing !== "iban" && <button type="button" className="portal-button portal-button-secondary" onClick={() => startEditing("iban")}>
                <i className="mdi mdi-pencil-outline" aria-hidden="true" />{t("edit")}
            </button>}
        </div>
    </div>;
};

const mapDispatchToProps = (dispatch: any) => bindActionCreators({ editCustomerIban, editCustomerVat }, dispatch);

export default connect(null, mapDispatchToProps)(AccountCustomerVatCheckCard);
